import { useEffect, useState } from "react";

/**
 * Collect AiSensy Business details (including password) before Embedded Signup.
 * Password is required later for Direct-API JWT (email:password:projectId).
 */
const AisensyBusinessSetupModal = ({ open, onClose, onSubmit, submitting = false }) => {
  const [form, setForm] = useState({
    display_name: "",
    email: "",
    company: "",
    contact: "",
    password: "",
    gstin: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setForm({
        display_name: user.name || user.company_name || "",
        email: user.email || "",
        company: user.company_name || user.name || "",
        contact: String(user.phone || user.contact || "").replace(/\D/g, ""),
        password: "",
        gstin: user.billing_gstin || user.tax_id || "",
      });
    } catch (_) {
      setForm({
        display_name: "",
        email: "",
        company: "",
        contact: "",
        password: "",
        gstin: "",
      });
    }
  }, [open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "gstin" ? value.toUpperCase() : value,
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const display_name = form.display_name.trim();
    const email = form.email.trim();
    const company = form.company.trim();
    const contact = form.contact.replace(/\D/g, "");
    const password = form.password;
    const gstin = form.gstin.trim().toUpperCase().replace(/\s+/g, "");

    if (!display_name || !email || !company || !contact || !password) {
      setError("Please fill all required fields.");
      return;
    }
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/.test(gstin)) {
      setError("Enter a valid 15-character GST number, or leave it blank.");
      return;
    }

    try {
      await onSubmit({
        display_name,
        email,
        company,
        contact,
        password,
        gstin: gstin || undefined,
        timezone: "Asia/Calcutta GMT+05:30",
        currency: "INR",
        companySize: "10 - 20",
      });
    } catch (err) {
      setError(err?.message || "Failed to create Blutec Ping business.");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-100 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Blutec Ping business setup</h2>
          <p className="mt-1 text-sm text-gray-500">
            Create your Blutec Ping business before connecting WhatsApp. Save this password — it is
            used to generate your messaging API token.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 px-5 py-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Display name</span>
            <input
              name="display_name"
              value={form.display_name}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              autoComplete="organization"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Business email</span>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              autoComplete="email"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Company</span>
            <input
              name="company"
              value={form.company}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              autoComplete="organization"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Contact (with country code)</span>
            <input
              name="contact"
              value={form.contact}
              onChange={handleChange}
              placeholder="9198XXXXXXXX"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              inputMode="numeric"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              GST number <span className="font-normal text-gray-400">(optional)</span>
            </span>
            <input
              name="gstin"
              value={form.gstin}
              onChange={handleChange}
              placeholder="24AAAAA0000A1Z5"
              maxLength={15}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase outline-none focus:border-gray-500"
              autoComplete="off"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Used on Ping invoices only. Not sent to WhatsApp setup.
            </span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Password</span>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              autoComplete="new-password"
              required
            />
            <span className="mt-1 block text-xs text-gray-500">
              At least 12 characters. Save it — it is stored securely and used for API access.
            </span>
          </label>

          {error ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Continue to WhatsApp"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AisensyBusinessSetupModal;
